<script setup lang="ts">
/**
 * Pricing rules table tab. Read-only.
 *
 * Infinite-scrolls `/rules` and renders a name + rule-text + start/end date
 * table. `isRuleFinished` determines whether `end_time` is in the past;
 * finished rows receive the muted `rowClass` so staff can visually distinguish
 * active rules at a glance. `AddButton` is shown but leads to no create form
 * (rules are managed server-side).
 */
import { computed, markRaw, ref, watch } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import AddButton from '@/components/table/AddButton.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import NameCell from '@/components/table/cell/NameCell.vue'
import TextCell from '@/components/table/cell/TextCell.vue'
import { useIsVisible } from '@/composables/useIsVisible'
import { fetchRules } from '@/api/rules'
import { fetchBusinessServiceTypes } from '@/api/businessServiceTypes'
import { rulesPollingInterval, serviceTypesPollingInterval } from '@/config/api'
import { defaultLocaleForDate } from '@/config/date'

interface RuleRow {
  // Row for the rules table, mapped from GET `/rules`.

  rowNumber: number
  // 1-based index over all loaded pages (same convention as the other tabs).

  name: string
  // Internal rule slug (e.g. `bachata_discount_rule_2025`); used as a stable identifier in the Name column.

  ruleText: string
  // Human-readable description of the rule, taken verbatim from `rule_text` returned by the backend.

  startDate?: string
  // Pre-formatted `start_time` date for display (e.g. `Nov 1, 2025`); `undefined` when absent.

  endDate?: string
  // Pre-formatted `end_time` date for display; `undefined` when the rule is open-ended.

  isFinished: boolean
  // True when `end_time` is in the past relative to the current moment; finished rows are rendered muted so the eye can lock onto still-active rules.
}

/**
 * Returns true when the rule's `end_time` is a valid ISO datetime in the past
 * (i.e. the rule no longer applies). `null` / missing / unparseable inputs
 * are treated as open-ended and therefore *not* finished, matching how the
 * backend renders `… repeats indefinitely.` for such rules. Compared against
 * the moment the `computed` re-runs, so the refetch cycle (via
 * `useInfiniteQuery`) naturally re-evaluates this when the data refreshes.
 */
function isRuleFinished(endIso: string | null | undefined): boolean {
  if (typeof endIso !== 'string' || endIso.trim() === '') return false
  const end = new Date(endIso).getTime()
  if (Number.isNaN(end)) return false
  return end <= Date.now()
}

/**
 * Formats an ISO datetime as a locale-aware, date-only label for the Start/End
 * columns. Rule boundaries from the backend are midnight-UTC (e.g.
 * `2025-11-01T00:00:00Z`) so we intentionally format in UTC: using the browser
 * timezone would drift the displayed calendar date by one in timezones west of
 * UTC (the user would see `2025-10-31`). `undefined` is returned for missing
 * or unparseable input so the cell can render its muted placeholder.
 */
function formatRuleDate(iso: string | null | undefined): string | undefined {
  if (typeof iso !== 'string' || iso.trim() === '') return undefined
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  return new Intl.DateTimeFormat(defaultLocaleForDate.value, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(d)
}

// Column widths sum to ~48rem (≈ `--width-middle-column` minus the
// article's 4rem horizontal padding) so this table has the same total
// width as every other tab's table; see `App.vue` for context.
const columns: Column<RuleRow>[] = [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: row.rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'name',
    label: 'Name',
    component: markRaw(NameCell),
    props: (row) => ({ name: row.name }),
    width: '14rem',
  },
  {
    key: 'ruleText',
    label: 'Rule',
    component: markRaw(TextCell),
    props: (row) => ({ value: row.ruleText, emptyPlaceholder: '–' }),
    width: '19rem',
  },
  {
    key: 'startDate',
    label: 'Start',
    component: markRaw(TextCell),
    props: (row) => ({ value: row.startDate, emptyPlaceholder: '–' }),
    width: '6rem',
  },
  {
    key: 'endDate',
    label: 'End',
    component: markRaw(TextCell),
    props: (row) => ({ value: row.endDate, emptyPlaceholder: '∞' }),
    width: '6rem',
  },
]

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['rules'],
  queryFn: ({ pageParam }) => fetchRules(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: rulesPollingInterval,
  refetchIntervalInBackground: false,
})

const {
  data: serviceTypesData,
  hasNextPage: serviceTypesHasNextPage,
  isFetchingNextPage: serviceTypesIsFetchingNextPage,
  fetchNextPage: serviceTypesFetchNextPage,
} = useInfiniteQuery({
  queryKey: ['businessServiceTypes'],
  queryFn: ({ pageParam }) => fetchBusinessServiceTypes(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  refetchInterval: serviceTypesPollingInterval,
  refetchIntervalInBackground: false,
})

watch(
  [serviceTypesHasNextPage, serviceTypesIsFetchingNextPage],
  ([more, fetching]) => {
    if (more && !fetching) void serviceTypesFetchNextPage()
  },
  { immediate: true },
)

/**
 * UUID → service type description map. Keys are string UUIDs from the API
 * (even though `BusinessServiceType.id` is typed `number`, at runtime it is
 * a UUID7 string). Used to substitute `lesson #<uuid>` in rule text.
 */
const serviceTypeNameById = computed<Map<string, string>>(() => {
  const map = new Map<string, string>()
  for (const page of serviceTypesData.value?.pages ?? []) {
    for (const group of page.items ?? []) {
      for (const type of group.business_service_types) {
        const name = type.description.trim()
        if (name) map.set(String(type.id), name)
      }
    }
  }
  return map
})

const LESSON_UUID_RE = /lesson\s+#([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi

function resolveRuleText(raw: string, nameById: Map<string, string>): string {
  return raw.replace(LESSON_UUID_RE, (_, uuid: string) => {
    const name = nameById.get(uuid.toLowerCase())
    return name ? `lesson "${name}"` : `lesson #${uuid}`
  })
}

/**
 * Table rows derived from the infinite-query cache: every loaded page is
 * flattened in order so "load more" appends rows without resetting indices.
 *
 * `rowNumber` is a 1-based running index across all pages (incremented once
 * per rule, not per page), so the "#" column stays stable while scrolling.
 *
 * `ruleText` falls back to an empty string when the API returns `null`/blank;
 * the `TextCell` then renders the muted placeholder `–`. Lesson UUIDs in the
 * backend-rendered text are substituted with their service type description.
 */
const data = computed<RuleRow[]>(() => {
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  let index = 0
  return pages.flatMap((page) =>
    (page.items ?? []).map((r) => {
      index += 1
      const raw = typeof r.rule_text === 'string' ? r.rule_text.trim() : ''
      return {
        rowNumber: index,
        name: r.name,
        ruleText: resolveRuleText(raw, serviceTypeNameById.value),
        startDate: formatRuleDate(r.start_time),
        endDate: formatRuleDate(r.end_time),
        isFinished: isRuleFinished(r.end_time),
      }
    }),
  )
})

/**
 * Per-row class hook for `DynamicTable`: active rules stay at full contrast,
 * finished rules get the `rules-row--finished` marker which the scoped style
 * block desaturates. Using a class rather than editing each cell keeps the
 * state in one place and avoids threading `isFinished` through every cell
 * component.
 */
function ruleRowClass(row: unknown): string | undefined {
  return (row as RuleRow).isFinished ? 'rules-row--finished' : undefined
}

const headerRef = ref<HTMLElement | null>(null)
const isHeaderVisible = useIsVisible(headerRef)

function onAddRule(): void {}
</script>

<template>
  <article>
    <div ref="headerRef" class="tab-header">
      <h1>Rules</h1>
      <AddButton label="Add rule" @click="onAddRule" />
    </div>
    <p v-if="isPending" class="text-tufte-muted">Loading rules…</p>
    <p v-else-if="isError" class="text-red-600">
      Failed to load rules: {{ (error as Error)?.message ?? 'Unknown error' }}
    </p>
    <template v-else>
      <DynamicTable
        class="rules-table"
        :columns="columns as Column<unknown>[]"
        :data="data"
        :has-next-page="hasNextPage"
        :is-fetching-next-page="isFetchingNextPage"
        :on-load-more="fetchNextPage"
        :row-class="ruleRowClass"
        :on-add="onAddRule"
        :compact-add-visible="!isHeaderVisible"
        storage-key="rules"
      />
    </template>
  </article>
</template>

<style scoped>
/*
 * The table stretches to fill the middle column (`width: 100%` default) so
 * that extra horizontal space the three-column layout gives the middle
 * column is reflected in the table itself. The Rule column is capped by
 * its `maxWidth` so long sentences still stay readable.
 */

.tab-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

/*
 * Rule sentences are long and dominate the row visually at the default
 * `--text-tufte-base`. Shrinking the third body cell to `--text-tufte-sm`
 * with a tighter line-height keeps multi-line descriptions compact while
 * the Name / Start / End columns retain the standard table scale.
 */
.rules-table :deep(tbody tr > td:nth-child(3)) {
  font-size: var(--text-tufte-sm);
  line-height: 1.35;
}

/*
 * Finished rules (end_time in the past) are de-emphasized so still-active
 * rules visually pop against them. We only tone the text colour and leave
 * the row background untouched so the Tufte hover rule from DynamicTable
 * still works. `:deep(...)` lets this scoped block reach into `<tr>` /
 * `<td>` rendered by the child DynamicTable component.
 */
.rules-table :deep(tr.rules-row--finished) > td,
.rules-table :deep(tr.rules-row--finished) > :is(td, th) * {
  color: var(--color-tufte-muted);
}
</style>
